'use client';

/**
 * Wishlist Button Component
 * 
 * A heart button that allows customers to add/remove items from their wishlist.
 * Shows login prompt if user is not authenticated.
 */

import { Heart, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { useWishlistStatus } from '@/hooks/useWishlist';
import type { WishlistEntityType } from '@/types/customer';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

// ============================================================================
// Types
// ============================================================================

export interface WishlistButtonProps {
  /** Type of entity */
  entityType: WishlistEntityType;
  /** ID of the entity */
  entityId: string;
  /** Display name of the entity — sent with the analytics event */
  entityName?: string | undefined;
  /** Monetary value (e.g. property price) — sent as conversion value */
  value?: number | undefined;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Variant style */
  variant?: 'ghost' | 'outline' | 'solid';
  /** Custom class name */
  className?: string;
  /** Auth URL for unauthenticated users (defaults to /register) */
  authUrl?: string;
  /** Callback when wishlist status changes */
  onChange?: (isInWishlist: boolean) => void;
  /** Show label text */
  showLabel?: boolean;
  /** Custom labels for Save/Saved text */
  labels?: { save?: string; saved?: string };
}

// ============================================================================
// Component
// ============================================================================

export function WishlistButton({
  entityType,
  entityId,
  entityName,
  value,
  size = 'md',
  variant = 'ghost',
  className = '',
  authUrl = '/register',
  onChange,
  showLabel = false,
  labels,
}: WishlistButtonProps) {
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading } = useCustomerAuth();
  const { isInWishlist, isLoading, toggle } = useWishlistStatus(entityType, entityId);

  // Size classes
  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  // Variant classes
  const variantClasses = {
    ghost: 'hover:bg-muted/50',
    outline: 'border border-border hover:bg-muted/50',
    solid: 'bg-background shadow-sm hover:bg-muted',
  };

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      // Redirect to register/login with return URL
      const returnUrl = typeof window !== 'undefined' ? window.location.pathname : '';
      router.push(`${authUrl}?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    await toggle();
    if (!isInWishlist) {
      analyticsEvents.addToWishlist(entityType, entityId, entityName, value);
    }
    onChange?.(!isInWishlist);
  };

  const isDisabled = authLoading || isLoading;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={`
        wishlist-button
        inline-flex items-center justify-center gap-2
        rounded-full
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${className}
      `}
      aria-label={isInWishlist ? 'Remove from wishlist' : 'Save to wishlist'}
    >
      {isLoading ? (
        <Loader2 className={`${iconSizes[size]} animate-spin text-muted-foreground`} />
      ) : (
        <Heart
          className={`
            ${iconSizes[size]}
            transition-colors duration-200
            ${isInWishlist 
              ? 'fill-rose-500 text-rose-500' 
              : 'text-muted-foreground hover:text-rose-500'
            }
          `}
        />
      )}
      {showLabel && (
        <span className="text-sm font-medium">
          {isInWishlist ? (labels?.saved || 'Saved') : (labels?.save || 'Save')}
        </span>
      )}
    </button>
  );
}

export default WishlistButton;
